import React, {useContext, useEffect, useImperativeHandle, useMemo, useRef, useState} from 'react';
import ReactDOM from 'react-dom';
import {useDropzone} from 'react-dropzone';
import classnames from '@src/helpers/classnames';
import Translate from '@src/components/Translate/Translate';
import trans from '@src/helpers/trans';
import {LocaleContext} from '@src/contexts/LocaleContext';
import AttachmentsPreviews from '../AttachmentsPreviews/AttachmentsPreviews';
import RenderIf from '@src/components/RenderIf/RenderIf';
import LoadingIndicator from '@src/components/LoadingIndicator/LoadingIndicator';
import CommentsList from '@src/components/CommentsList/CommentsList';
import ValidationHttpException from '@src/exceptions/ValidationHttpException';
import {MessageReceiverProps} from '@src/@types/components/Chat/MessageReceiver';
import useUniqueId from '@src/hooks/useUniqueId';
import ReplyToMessage from '@src/components/Chat/ReplyToMessage/ReplyToMessage';

export function makeOnDropAttachListener(
        setAttachments: React.Dispatch<React.SetStateAction<File[]>>
): React.ComponentProps<typeof MessageReceiver>['onDrop'] {
    return (attachments) => {
        setAttachments(list => [...list, ...attachments]);
    };
}

export function makeOnDeleteAttachListener(
        setAttachments: React.Dispatch<React.SetStateAction<File[]>>
): React.ComponentProps<typeof AttachmentsPreviews>['onDelete'] {
    return (index) => {
        setAttachments(list => {
            list.splice(index, 1);

            return [...list];
        });
    };
}

export function makeOnSubmitListener(
        onPostMessage: React.ComponentProps<typeof CommentsList>['onPostMessage'],
        attachments: File[],
        setAttachments?: React.Dispatch<React.SetStateAction<File[]>>
): React.ComponentProps<typeof MessageReceiver>['onSubmit'] {
    return onPostMessage ? (message, answerTo) => {
        return onPostMessage(message, attachments, answerTo).then(value => {
            if (setAttachments) {
                setAttachments([]);
            }

            return value;
        });
    } : undefined;
}

const settings: {
    sendByCtrl?: boolean,
    sendByAlt?: boolean,
    sendByShift?: boolean,
    sendByMeta?: boolean,
} = {
    sendByCtrl: true,
};

const helperStyles = {opacity: 0, wordBreak: 'break-all'} as React.CSSProperties;

function Helper({portalKey, innerRef, ...props}: React.HTMLAttributes<HTMLDivElement> & {
    innerRef?: React.RefObject<HTMLDivElement>,
    portalKey?: string,
}) {
    return ReactDOM.createPortal(<div {...props} ref={innerRef}/>, document.body, portalKey);
}

const MemoizedHelper = React.memo(Helper);

export default function MessageReceiver(props: MessageReceiverProps) {
    const {locale} = useContext(LocaleContext);
    const uniqueId = useUniqueId();

    const helperRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const isLoadingRef = useRef(false);
    const valueRef = useRef<string>(props.defaultValue ?? props.value ?? '');

    const [isEmpty, setIsEmpty] = useState(props.value == null);
    const [isHeightAuto, setIsHeightAuto] = useState(true);
    const [fieldHeight, setFieldHeight] = useState<number | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isInvalid, setIsInvalid] = useState(props.isInvalid);
    const [errorMessage, setErrorMessage] = useState<string | null>(props.errorMessage ?? null);

    useImperativeHandle(props.apiRef, () => ({focus: () => void inputRef.current?.focus()}), []);

    useEffect(() => void setErrorMessage(props.errorMessage ?? null), [props.errorMessage]);

    useEffect(() => void setIsInvalid(props.isInvalid), [props.isInvalid]);

    const onInputHandler = props.onInput;
    const onSubmitHandler = props.onSubmit;
    const onErrorHandler = props.onError ?? setErrorMessage;
    const baseOnDropHandler = props.onDrop;
    const onResetAnswerTo = props.onResetAnswerTo;

    const [onChange, onSubmit, onKeyDown, onDrop] = useMemo(() => {
        const setValue = (value: string | null): void => {
            valueRef.current = value ?? '';
            const isEmpty = !valueRef.current.trim().length;

            setIsEmpty(isEmpty);
            onInputHandler(isEmpty ? null : valueRef.current);
        };

        const onSubmit = onSubmitHandler ? (() => {
            if (isLoadingRef.current) {
                return;
            }

            setIsLoading(isLoadingRef.current = true);
            onErrorHandler(null);
            setIsInvalid(false);

            const value = valueRef.current;

            onSubmitHandler(
                    value.length ? value : null,
                    props.itIsAnswerTo ?? props.itIsAnswerToMessage?.id ?? null,
            ).then(value => {
                setIsHeightAuto(true);
                setFieldHeight(null);

                setValue(null);

                if (value === false) {
                    setIsInvalid(true);
                } else if (onResetAnswerTo && props.itIsAnswerToMessage?.id != null) {
                    onResetAnswerTo();
                }
            }).catch(e => {
                if (e === false) {
                    setIsInvalid(true);

                    return false;
                }

                if (e instanceof ValidationHttpException) {
                    const messages = Object.values(e.getValidationErrors()).at(0);
                    const message = messages?.length ? messages.at(0) : null;

                    onErrorHandler(message ?? 'Something went wrong');
                } else {
                    onErrorHandler(e.message);
                }

                return false;
            }).finally(() => {
                setIsLoading(isLoadingRef.current = false);
            });
        }) : undefined;

        const onChange = (e: React.FormEvent<HTMLTextAreaElement>) => {
            const value = e.currentTarget.value;

            setValue(value);

            setIsInvalid(false);
            onErrorHandler(null);

            const helper = helperRef.current;
            const input = inputRef.current;
            if (!helper || !input) {
                return;
            }

            helper.innerHTML = value.replace(/\n+?/gum, '<br/>') + '&nbsp;';
            helper.classList.remove('d-none');
            helper.style.width = input.offsetWidth + 'px';

            const helperHeight = helper.getBoundingClientRect().height;
            const height = Math.max(14, Math.min(helperHeight, 200));
            helper.classList.add('d-none');

            setFieldHeight(value.length ? height : null);
            setIsHeightAuto(helperHeight > height);
        };

        const onKeyDown = (e: React.FormEvent<HTMLTextAreaElement>) => {
            const event = e.nativeEvent;
            if (
                    event &&
                    onSubmit &&
                    event instanceof KeyboardEvent &&
                    event.key === 'Enter' &&
                    settings && (
                            settings.sendByCtrl && event.ctrlKey ||
                            settings.sendByAlt && event.altKey ||
                            settings.sendByShift && event.shiftKey ||
                            settings.sendByMeta && event.metaKey
                    )
            ) {
                onSubmit();

                return;
            }

            if (
                    event &&
                    onResetAnswerTo &&
                    props.itIsAnswerToMessage?.id != null &&
                    event instanceof KeyboardEvent &&
                    event.key === 'Escape'
            ) {
                onResetAnswerTo();

                return;
            }
        };

        const onDrop = (files: FileList | File[]) => {
            if (isLoadingRef.current) {
                return;
            }

            if (files instanceof FileList) {
                files = [].slice.call(files);
            }

            return baseOnDropHandler(files);
        };

        return [onChange, onSubmit, onKeyDown, onDrop];
    }, [baseOnDropHandler, onResetAnswerTo, onErrorHandler, onInputHandler, onSubmitHandler, props.itIsAnswerTo, props.itIsAnswerToMessage?.id]);

    const {getInputProps, getRootProps, isDragActive, open} = useDropzone({
        disabled: props.isDisabled || isLoading,
        onDrop,
        noClick: true,
        noKeyboard: true,
        preventDropOnDocument: false,
    });

    const {itIsAnswerToMessage} = props;

    return <>
        <div className={classnames('chat__dropzone chat__dropzone--plain', {
            [`chat__dropzone--${props.size}`]: props.size,
            'is-invalid': isInvalid,
        })} {...getRootProps()} tabIndex={undefined}>
            <MemoizedHelper
                    className={classnames('chat__message-receiver__message-field d-none', {
                        [`chat__message-receiver__message-field--${props.size}`]: props.size,
                    })}
                    style={helperStyles}
                    innerRef={helperRef}
                    portalKey={uniqueId}
            />

            <input {...getInputProps({multiple: true})}/>

            {!isDragActive ? null : (
                    <div className={classnames('chat__dropzone__drop-file-here-message', {
                        'chat__dropzone__drop-file-here-message--visible': isDragActive,
                    })}>
                        <Translate message="Chat:MessageReceiver.drop-file-here"/>
                    </div>
            )}

            <ReplyToMessage message={itIsAnswerToMessage} onCancelButtonClick={onResetAnswerTo}/>

            <div className={classnames('chat__message-receiver', {
                'chat__message-receiver--loading': isLoading,
                'chat__message-receiver--invalid': isInvalid,
                'chat__message-receiver--disabled': props.isDisabled,
                'chat__message-receiver--reply-to': props.itIsAnswerToMessage != null,
                [`chat__message-receiver--${props.size}`]: props.size,
            })}>
                <div className="chat__message-receiver__attach-button-container">
                    <button
                            type="button"
                            className="chat__message-receiver__attach-button"
                            disabled={props.isDisabled}
                            onClick={open}
                    >
                        <i className="fa fa-paperclip"/>
                    </button>
                </div>

                <div className="chat__message-receiver__message-field-container">
                    <textarea
                            ref={inputRef}
                            rows={1}
                            style={{height: fieldHeight ?? undefined}}
                            className={classnames('chat__message-receiver__message-field', {
                                'chat__message-receiver__message-field--empty': isEmpty,
                                'chat__message-receiver__message-field--height-auto': isHeightAuto,
                                [`chat__message-receiver__message-field--${props.size}`]: props.size,
                            })}
                            onChange={onChange}
                            onKeyDown={onKeyDown}
                            value={props.value ?? valueRef.current ?? ''}
                            placeholder={
                                props.placeholder ?
                                        trans(props.placeholder, {}, null, locale) :
                                        undefined
                            }
                            onPaste={event => {
                                onDrop(Array.from(event.clipboardData.files));
                            }}
                            disabled={props.isDisabled || isLoading}
                            form={props.formId}
                    />
                </div>

                <RenderIf expression={onSubmit}>
                    <div className="chat__message-receiver__send-button-container">
                        <button
                                type="button"
                                className="chat__message-receiver__send-button"
                                disabled={props.isDisabled || isLoading}
                                onClick={onSubmit}
                        >
                            {
                                isLoading ?
                                        <LoadingIndicator size="sm" type="border"/> :
                                        <i className="fa fa-paper-plane"/>
                            }
                        </button>
                    </div>
                </RenderIf>
            </div>

            <div>{props.filesPreviewSlot}</div>
        </div>

        {errorMessage ? (
                <div className="chat__error-message">
                    <Translate message={errorMessage}/>
                </div>
        ) : null}
    </>;
}
